import { Alert, Box, IconButton } from "@mui/material";
import { useReactFlow } from "@xyflow/react";
import { useEffect, useState } from "react";
import { axiosInstance } from "../../services/api/api";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

// components
import { CardName } from "./card-name";
import { LoaderMUI } from "../../assets/global/loader/loader-mui";
import { AlertMessage } from "../../assets/global/alert/alert-message";
import { DeleteConfirmationDialog } from "../ui/confirm-delete";
import { BasicSelectType } from "../ui/dropdown-type";

// buildings
import { Warehouse } from "./areas/warehouse";
import { Logistic } from "./areas/logistic";
import { Infrastructure } from "./areas/infrastructure";
import { Highbay } from "./areas/highbay";
import { Production } from "./areas/production";
import { Carparking } from "./areas/carparking";
import { Truckparking } from "./areas/truckparking";
import { Office } from "./areas/office";

// services
import { generateDefaultBuildingName } from "../../utils/planner";
import { usePlannerContext } from "../../services/context/planner-context";
import { luckyorangeTrackEven } from "../../services/lucky-orange/luckyorange-events";

// constants
import { TEXT } from "../../constants/text";

// svg
import { ReactComponent as IconDrag } from "../../assets/svg/drag.svg";
import { ReactComponent as IconEdit } from "../../assets/svg/edit.svg";
import { ReactComponent as IconDelete } from "../../assets/svg/delete.svg";
import { ReactComponent as IconCheck } from "../../assets/svg/check.svg";
import { ReactComponent as IconCopy } from "../../assets/svg/copy.svg";
import { ReactComponent as IconChevronDown } from "../../assets/svg/chevron-down.svg";
import { TooltipCustom } from "../ui/tooltip-custom";
import { useUserContext } from "../../services/context/user-context";
import { CAR_PARKING_TYPE } from "../../constants/global.constants";
import { COLORS_GLOBAL } from "../../constants/colors.constants";

export const AreaCard = ({ nodeId, nodeData }) => {
  const { projectId } = useParams();
  const { userData, viewOnly } = useUserContext();

  const reactFlowInstance = useReactFlow();
  const {
    nodes,
    setNodes,
    setEdges,
    setValidateEnd,
    setValidateStart,
    buildingsTypes,
  } = usePlannerContext();

  /* const hasCarPark = !!nodes.find(
    (item) => nodeData.type === CAR_PARKING_TYPE
  ); */
  const buildingsCount = nodes.filter(
    (item) => item.data.type !== CAR_PARKING_TYPE
  ).length;

  const currentNode = reactFlowInstance.getNode(nodeId);

  const [editMode, setEditMode] = useState(false);

  const [loading, setLoading] = useState(nodeData.buildingId ? false : true);
  const [errorCreate, setErrorCreate] = useState(null);
  const [errorDelete, setErrorDelete] = useState(null);

  const createBuilding = async (name) => {
    const payload = {
      projectId: projectId,
      name: name,
      buildingType: nodeData.typeId,
      buildingMiroPosition: {
        id: currentNode.id,
        type: currentNode.type,
        position: currentNode.position,
      },
      stories: nodeData.building.stories,
      isolated: nodeData.building.isolated,
    };

    try {
      const response = await axiosInstance.post(
        "/buildings/createBuilding",
        payload
      );

      const buildingId = response.data ? response.data.buildingId : null;
      setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === nodeId) {
            return {
              ...node,
              data: {
                ...node.data,
                buildingId,
                building: {
                  ...node.data.building,
                  id: buildingId,
                  name: payload.name,
                  buildingType: payload.buildingType,
                  buildingMiroPosition: payload.buildingMiroPosition,
                },
              },
            };
          }
          return node;
        })
      );
      setValidateStart(0);
      setValidateEnd(0);
    } catch (error) {
      // Handle error
      setErrorCreate(
        error.response ? error.response.data.message : error.message
      );
      deleteNodeOnError();
      /* setNodes((nds) => nds.filter((node) => node.id !== nodeId)); */
    } finally {
      setLoading(false);
    }
  };

  const deleteNodeOnError = () => {
    setTimeout(() => {
      if (nodeId) {
        setNodes((nds) => nds.filter((node) => node.id !== nodeId));
      }
    }, 6000);
  };

  const deleteBuilding = async (buildingId) => {
    try {
      const response = await axiosInstance.delete(
        `/buildings/deleteBuilding/${buildingId}`
      );

      setNodes((nds) => nds.filter((node) => node.id !== nodeId));
      setEdges((eds) =>
        eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId)
      );
    } catch (error) {
      // Handle error
      setErrorDelete(
        error.response ? error.response.data.message : error.message
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const nodeExist = nodeData.buildingId;
    if (nodeData && projectId && currentNode && !!!nodeExist) {
      const name = generateDefaultBuildingName(
        nodeData.typeId,
        nodeData.typeName,
        nodes
      );

      setLoading(true);
      setErrorCreate(null);
      createBuilding(name);
      luckyorangeTrackEven("Planner: create new building");
    }
  }, []);

  const handleDeleteNode = () => {
    if (nodeData.building.id) {
      setLoading(true);
      setErrorDelete(null);
      deleteBuilding(nodeData.building.id);
      luckyorangeTrackEven("Planner: delete building");
    }
  };

  const handleChangeType = (selectedTypeObj, buildingInputs) => {
    setNodes((nodes) =>
      nodes.map((node) => {
        if (node.id === nodeId) {
          return {
            ...node,
            data: {
              ...node.data,
              typeId: selectedTypeObj.id,
              type: selectedTypeObj.node_name,
              typeName: selectedTypeObj.display_name,
              color: selectedTypeObj.color,
              building: {
                ...node.data.building,
                ...buildingInputs,
              },
            },
          };
        }
        return node;
      })
    );
  };

  const areaComponent = {
    logistic: <Logistic area={nodeData} nodeId={nodeId} />,
    infrastructure: <Infrastructure area={nodeData} nodeId={nodeId} />,
    warehouse: <Warehouse area={nodeData} nodeId={nodeId} />,
    highbay: <Highbay area={nodeData} nodeId={nodeId} />,
    office: <Office area={nodeData} nodeId={nodeId} />,
    production: <Production area={nodeData} nodeId={nodeId} />,
    carparking: <Carparking area={nodeData} nodeId={nodeId} />,
    truckparking: <Truckparking area={nodeData} nodeId={nodeId} />,
  };

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleOpenDialog = () => {
    if (nodeData.buildingId) {
      setIsDialogOpen(true);
    } else {
      setNodes((nds) => nds.filter((node) => node.id !== nodeId));
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleDelete = () => {
    handleDeleteNode();
    setIsDialogOpen(false);
  };

  const editModeHandler = () => {
    if (!editMode) {
      setEditMode(true);
    }
  };
  function shiftPosition({ x, y }) {
    return {
      x: x + 500,
      y: y,
    };
  }

  const duplicateBuilding = async (duplicatedNode, name) => {
    const payloadCreate = {
      projectId: projectId,
      name: name,
      buildingType: duplicatedNode.typeId,
      buildingMiroPosition: duplicatedNode.building.buildingMiroPosition,
      stories: nodeData.building.stories,
      isolated: nodeData.building.isolated,
    };

    try {
      const responseCreate = await axiosInstance.post(
        "/buildings/createBuilding",
        payloadCreate
      );

      const buildingId = responseCreate.data
        ? responseCreate.data.buildingId
        : null;

      if (buildingId) {
        const payloadUpdate = {
          ...duplicatedNode.building,
          id: buildingId,
        };

        const responseUpdate = await axiosInstance.put(
          `/buildings/updateBuilding/${buildingId}`,
          payloadUpdate
        );

        setNodes((nodes) =>
          nodes.concat({
            id: duplicatedNode.building.buildingMiroPosition.id,
            type: "customNode",
            position: duplicatedNode.building.buildingMiroPosition.position,
            deletable: false,
            data: {
              ...duplicatedNode,
              buildingId: buildingId,
              connectionClickStartHandle: null,
              building: {
                ...duplicatedNode.building,
                id: buildingId,
              },
            },
          })
        );
      }

      setValidateStart(0);
      setValidateEnd(0);
    } catch (error) {
      // Handle error
      setErrorCreate(
        error.response ? error.response.data.message : error.message
      );
      deleteNodeOnError();
      /* setNodes((nds) => nds.filter((node) => node.id !== nodeId)); */
    } finally {
      setLoading(false);
    }
  };

  const duplicateBuildingHandler = () => {
    if (nodeData && projectId && currentNode) {
      const name = generateDefaultBuildingName(
        nodeData.typeId,
        nodeData.typeName,
        nodes
      );

      /* setLoading(true);
      setErrorCreate(null);
      createBuilding(name); */
      luckyorangeTrackEven("Planner: duplicate building");
      const newPosition = shiftPosition(
        nodeData.building.buildingMiroPosition.position
      );
      const duplicatedNode = {
        ...nodeData,
        building: {
          ...nodeData.building,
          buildingMiroPosition: {
            id: uuidv4(),
            type: "customNode",
            position: newPosition,
          },
          name: name,
        },
      };

      duplicateBuilding(duplicatedNode, name);
    }
  };

  const disableDuplicateBtn = !userData.isAdmin
    ? nodeData.type === CAR_PARKING_TYPE
      ? true
      : buildingsCount >= 10
    : false;

  return (
    <Box
      key={nodeId + "area-card"}
      sx={{
        width: "380px",
        height: "100%",
        background: "#FBFBFD",
        display: "flex",
        flexDirection: "column",
        boxShadow: 3,
        borderRadius: "16px",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "auto",
          minHeight: "52px",
          background: nodeData.color,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          padding: "8px",
          gap: "24px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: "8px",
            width: "100%",
            mt: "2px",
          }}
        >
          <Box
            sx={{
              mt: "4px",
            }}
          >
            <IconDrag fill="#fff" />
          </Box>

          <CardName
            nodeId={nodeId}
            nodeData={nodeData}
            editMode={viewOnly ? false : editMode}
            setEditMode={setEditMode}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            mt: "2px",
          }}
        >
          <TooltipCustom
            text={editMode ? "Save Name" : "Edit Name"}
            placement="top"
          >
            <Box>
              <IconButton onClick={editModeHandler} disabled={viewOnly}>
                {editMode ? (
                  <IconCheck stroke="#fff" width={"18px"} height={"18px"} />
                ) : (
                  <IconEdit
                    fill={viewOnly ? "#b1b1b7" : "#fff"}
                    width={"18px"}
                    height={"18px"}
                  />
                )}
              </IconButton>
            </Box>
          </TooltipCustom>
          {/* <IconButton onClick={() => setEditMode((prev) => !prev)}>
            <IconCheck stroke="#fff" width={"18px"} height={"18px"} />
          </IconButton> */}
          <TooltipCustom text={"Delete Function Type"} placement="top">
            <Box>
              <IconButton onClick={handleOpenDialog} disabled={viewOnly}>
                <IconDelete
                  fill={viewOnly ? "#b1b1b7" : "#fff"}
                  width={"18px"}
                  height={"18px"}
                />
              </IconButton>
            </Box>
          </TooltipCustom>
          <TooltipCustom text={"Duplicate Function Type"} placement="top">
            <Box>
              <IconButton
                disabled={disableDuplicateBtn || viewOnly}
                onClick={duplicateBuildingHandler}
              >
                <IconCopy
                  stroke={disableDuplicateBtn || viewOnly ? "#b1b1b7" : "#fff"}
                  width={"18px"}
                  height={"18px"}
                />
              </IconButton>
            </Box>
          </TooltipCustom>
        </Box>
      </Box>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          padding: "20px 16px",
        }}
      >
        {/* Loader */}
        {loading && (
          <Box sx={{ width: "100%", height: "80px", position: "relative" }}>
            <LoaderMUI withBackdrop={false} />
          </Box>
        )}

        {/* Error Alert */}
        {errorCreate && (
          <Box
            sx={{
              width: "100%",
              height: "80px",
              position: "relative",
              margin: "0 auto",
            }}
          >
            <Alert severity={"error"} sx={{ width: "100%" }}>
              Network error while creating the{" "}
              {nodeData.type === "carparking" ||
              nodeData.type === "truckparking"
                ? "area"
                : "building"}
              . Please try again.
            </Alert>
          </Box>
        )}

        {!loading && !errorCreate /* && nodeData.building */ && (
          <>
            {errorDelete && (
              <Box
                sx={{
                  width: "100%",
                  position: "relative",
                  margin: "0 auto",
                }}
              >
                <Alert severity={"error"} sx={{ width: "100%" }}>
                  Network error while deleting the{" "}
                  {nodeData.type === "carparking" ||
                  nodeData.type === "truckparking"
                    ? "area"
                    : "building"}
                  . Please try again.
                </Alert>
              </Box>
            )}
            <BasicSelectType
              key={nodeId + "dropdownType"}
              label={"Function Type"}
              unit={null}
              value={nodeData.typeId}
              setValue={handleChangeType}
              list={
                buildingsTypes.filter((item) => item.displayFrontend)
                /* .map((item) => item.display_name) */
              }
              setLoading={setLoading}
              nodeData={nodeData}
              type={nodeData.type}
              tooltip={
                nodeData.type === "carparking"
                  ? TEXT.planner.inputs.functionTypeCarParkingTooltip
                  : TEXT.planner.inputs.functionTypeTooltip
              }
              disabled={viewOnly}
            />
            {areaComponent[nodeData.type]}
            <DeleteConfirmationDialog
              open={isDialogOpen}
              onClose={handleCloseDialog}
              onDelete={handleDelete}
              name={nodeData.building?.name}
              type={"functionType"}
            />
          </>
        )}
      </Box>
    </Box>
  );
};
